import { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef } from "react";
import api, { formatApiError } from "../api";

const AuthContext = createContext(null);

// Intervalo mínimo entre dois /auth/refresh (evita hammering em
// visibilitychange que dispara múltiplos eventos em sequência no iOS).
const REFRESH_THROTTLE_MS = 2 * 60 * 60 * 1000; // 2 horas

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [support, setSupport] = useState(null); // metadata da sessão de suporte, quando ativa
  const [loading, setLoading] = useState(true);
  const lastRefreshAt = useRef(0);

  const fetchMe = useCallback(async () => {
    const supportToken = typeof sessionStorage !== "undefined" ? sessionStorage.getItem("flr_support_token") : null;
    const normalToken = localStorage.getItem("flr_token");
    if (!supportToken && !normalToken) {
      setUser(null); setTenant(null); setSupport(null); setLoading(false);
      return;
    }
    try {
      if (supportToken) {
        const { data } = await api.get("/support/me");
        setUser({
          id: `super:${data.super_admin.id}`,
          name: `Suporte · ${data.super_admin.name || data.super_admin.email}`,
          email: data.super_admin.email,
          role: "admin",
          admin_level: "master",
          support_mode: true,
        });
        setTenant({
          id: data.tenant.id,
          slug: data.tenant.slug,
          name: data.tenant.display_name,
          display_name: data.tenant.display_name,
          tv_display_name: data.tenant.display_name,
          logo_url: data.tenant.logo_url,
          segment: data.tenant.segment,
          client_code: data.tenant.client_code,
        });
        setSupport(data);
      } else {
        const [me, t] = await Promise.all([
          api.get("/auth/me"),
          api.get("/tenant/me").catch(() => ({ data: null })),
        ]);
        setUser(me.data);
        setTenant(t.data);
        setSupport(null);
      }
    } catch (e) {
      // Só desloga em resposta INEQUÍVOCA de auth inválida (401/403).
      // Erros transitórios (network error, timeout, 5xx) preservam o token —
      // o próximo visibilitychange/online refaz o fetchMe automaticamente.
      const status = e?.response?.status;
      const isAuthFailure = status === 401 || status === 403;
      if (isAuthFailure) {
        if (supportToken) sessionStorage.removeItem("flr_support_token");
        else localStorage.removeItem("flr_token");
        setUser(null); setTenant(null); setSupport(null);
      }
      // eslint-disable-next-line no-console
      if (!isAuthFailure) console.warn("[auth] fetchMe falhou (transitório, token preservado)", status || e?.message);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Renovação silenciosa do token. Chamada em visibilitychange/online com
   * throttle. Falha silenciosa (não desloga) — se token estiver realmente
   * inválido, a próxima chamada protegida devolve 401 e o fluxo normal roda.
   * Em Modo Suporte, pulamos (backend retorna 400).
   */
  const silentRefresh = useCallback(async () => {
    const supportToken = typeof sessionStorage !== "undefined" ? sessionStorage.getItem("flr_support_token") : null;
    if (supportToken) return; // suporte não renova
    const normalToken = localStorage.getItem("flr_token");
    if (!normalToken) return;
    const now = Date.now();
    if (now - lastRefreshAt.current < REFRESH_THROTTLE_MS) return;
    lastRefreshAt.current = now;
    try {
      const { data } = await api.post("/auth/refresh");
      if (data?.token) {
        localStorage.setItem("flr_token", data.token);
      }
    } catch (e) {
      // 401 → token já expirou de verdade; o próximo /auth/me vai limpar o token.
      // 400 (Modo Suporte) ou 5xx/network: ignora.
      // eslint-disable-next-line no-console
      console.warn("[auth] refresh falhou", e?.response?.status || e?.message);
    }
  }, []);

  useEffect(() => { fetchMe(); }, [fetchMe]);

  // Recupera sessão ao voltar foco ou rede. Importante para iOS Safari e PWAs
  // standalone — ao desbloquear o celular, 'visibilitychange' dispara.
  useEffect(() => {
    const onVisible = () => {
      if (typeof document !== "undefined" && document.visibilityState === "visible") {
        // Tenta renovar silenciosamente; depois reconcilia o estado local.
        silentRefresh().finally(() => fetchMe());
      }
    };
    const onOnline = () => { silentRefresh().finally(() => fetchMe()); };
    if (typeof document !== "undefined") document.addEventListener("visibilitychange", onVisible);
    if (typeof window !== "undefined") window.addEventListener("online", onOnline);
    return () => {
      if (typeof document !== "undefined") document.removeEventListener("visibilitychange", onVisible);
      if (typeof window !== "undefined") window.removeEventListener("online", onOnline);
    };
  }, [silentRefresh, fetchMe]);

  const login = useCallback(async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("flr_token", data.token);
      lastRefreshAt.current = Date.now();
      setUser(data.user);
      setTenant(data.tenant || null);
      setSupport(null);
      return { ok: true, user: data.user, tenant: data.tenant };
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  }, []);

  const logout = useCallback(async () => {
    const isSupport = typeof sessionStorage !== "undefined" && sessionStorage.getItem("flr_support_token");
    if (isSupport) {
      try { await api.post("/support/logout"); } catch (e) { /* silencioso */ }
      sessionStorage.removeItem("flr_support_token");
    } else {
      localStorage.removeItem("flr_token");
    }
    lastRefreshAt.current = 0;
    setUser(null); setTenant(null); setSupport(null);
  }, []);

  const value = useMemo(
    () => ({ user, tenant, support, loading, login, logout, refresh: fetchMe }),
    [user, tenant, support, loading, login, logout, fetchMe],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
